/* The dashboard's reference "now".
 *
 * Time-range presets (5m … 30d) are computed relative to this instant.
 * Against the real API we anchor to the actual wall clock so presets line up
 * with live data; against the mock we use a fixed instant so the seeded data
 * universe is stable and tests stay deterministic. */

import { USE_REAL_API } from "./config";

const MOCK_NOW = new Date("2026-06-04T12:00:00Z");

export const DASHBOARD_NOW = USE_REAL_API ? new Date() : MOCK_NOW;

/** Reference "now", re-read per call (unlike the module-load DASHBOARD_NOW
 *  constant) so a refresh can roll preset windows forward. Fixed in mock mode. */
export function dashboardNow(): Date {
  return USE_REAL_API ? new Date() : MOCK_NOW;
}

/** Freshness instant for the chrome. The real API reports true freshness per
 *  response via meta.data_through; this is the default until a response lands. */
export const DATA_THROUGH = USE_REAL_API
  ? new Date()
  : new Date("2026-06-04T09:42:00Z");

export const DAY_MS = 86_400_000;
export const HOUR_MS = 3_600_000;

/** Date-only UTC bound (`YYYY-MM-DD`) — the gateway reads it as a whole UTC day. */
export function dateOnly(d: Date): string {
  return d.toISOString().slice(0, 10);
}

/** Second-precision UTC bound (`YYYY-MM-DDTHH:mm:ssZ`) — the contract's ISO-8601
 *  datetime form, used for sub-day windows. */
export function isoSecond(d: Date): string {
  return `${d.toISOString().slice(0, 19)}Z`;
}

/* Epoch ms from a backend timestamp that may carry no UTC offset.
 *
 * The gateway's settings API serializes its audit columns from naive datetimes
 * (`base_model.py` uses `types.DateTime` with `datetime.utcnow()`), so they
 * arrive as `2026-08-03T18:49:22.640749` — UTC in fact, but unmarked. Per
 * ECMAScript, `Date.parse` reads an offset-less date-TIME as LOCAL time (only
 * date-only strings default to UTC), so parsing one directly is wrong by the
 * viewer's offset everywhere except UTC. That silently disabled the whole
 * priming feature for every non-UTC user (OPSS-323).
 *
 * Requires a time component. Appending `Z` to a date-only string yields
 * `2026-08-03Z`, which is outside the spec's simplified-ISO grammar and so
 * implementation-defined — V8 happens to accept it as UTC midnight, but don't
 * rely on that: never pass `dateOnly()` output here.
 *
 * Sub-millisecond precision is dropped: V8 truncates the backend's microseconds
 * to ms (`.640749` -> `.640`), which is far finer than anything we compare. */
export function utcMs(timestamp: string): number {
  const hasOffset = /(Z|[+-]\d{2}:?\d{2})$/.test(timestamp);
  return Date.parse(hasOffset ? timestamp : `${timestamp}Z`);
}

/** Whether a bound carries a time-of-day component (datetime vs. date-only). */
export function hasTime(bound: string): boolean {
  return bound.includes("T");
}
